import React, {useEffect, useState} from "react";
import styles, {C} from "../styles";
import {apiGetInvite, apiJoinByInvite} from "../api/invites";
import {apiGetPseudoSuggestions, apiResendVerification} from "../api/auth";
import {validatePassword} from "../utils";
import PseudoWaitlistNotice from "../components/PseudoWaitlistNotice";
import CguAcceptance from "../components/CguAcceptance";
import EmailVerificationNotice from "../components/EmailVerificationNotice";
import {trackSignupSucceeded} from "../analytics";

export default function InvitePage({token, user, login, register, onGoToApp, onRefreshGroups}) {
    const [invite, setInvite] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [joining, setJoining] = useState(false);
    const [joined, setJoined] = useState(false);
    const [joinMessage, setJoinMessage] = useState("");

    const [authMode, setAuthMode] = useState("login");
    const [email, setEmail] = useState("");
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [authError, setAuthError] = useState("");
    const [pendingVerification, setPendingVerification] = useState(null);
    const [suggestions, setSuggestions] = useState([]);
    const [selectedPseudo, setSelectedPseudo] = useState(null);
    const [loadingSuggestions, setLoadingSuggestions] = useState(false);
    const [cguAccepted, setCguAccepted] = useState(false);

    const pwdCheck = validatePassword(password);
    const canRegister = !!(email && selectedPseudo && pwdCheck.valid && cguAccepted);
    const showRegisterForm = authMode === "login" || suggestions.length > 0;

    useEffect(() => {
        apiGetInvite(token).then(data => {
            if (data.error) setError(data.error);
            else setInvite(data);
            setLoading(false);
        });
    }, [token]);

    useEffect(() => {
        if (authMode !== "register") return;
        refreshSuggestions();
        setCguAccepted(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authMode]);

    function switchAuthMode(mode) {
        setPendingVerification(null);
        setAuthMode(mode);
    }

    function refreshSuggestions() {
        setLoadingSuggestions(true);
        setSelectedPseudo(null);
        apiGetPseudoSuggestions().then(data => {
            setSuggestions(Array.isArray(data) ? data : []);
            setLoadingSuggestions(false);
        });
    }

    async function handleJoin() {
        setJoining(true);
        const data = await apiJoinByInvite(token);
        if (data.error) {
            setJoinMessage(data.error);
        } else {
            setJoined(true);
            setJoinMessage(data.already_member
                ? "Vous êtes déjà membre de ce collectif !"
                : `Bienvenue dans ${data.group_name} !`);
            onRefreshGroups();
        }
        setJoining(false);
    }

    async function handleAuthThenJoin() {
        setAuthError("");
        setPendingVerification(null);
        if (authMode === "login") {
            const result = await login(username, password);
            if (result?.error) {
                setAuthError(result.error);
                return;
            }
            setTimeout(() => handleJoin(), 300);
            return;
        }

        let mailFailed = false;
        const registerError = await register(email, selectedPseudo.pseudo, password, cguAccepted, () => { mailFailed = true; });
        if (registerError) {
            setAuthError(registerError);
            return;
        }
        trackSignupSucceeded();
        // Double opt-in : le compte est créé mais inactif tant que l'email pro
        // n'est pas vérifié — pas de token disponible, donc pas de join possible ici.
        setUsername(selectedPseudo.pseudo);
        setPendingVerification({email: email.trim(), pseudo: selectedPseudo.pseudo, mailFailed});
        setAuthMode("login");
    }

    if (loading) {
        return (
            <div style={{...styles.page, display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh"}}>
                <p style={{color: C.textMuted, fontSize: 16}}>Chargement de l'invitation...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{...styles.page, display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh"}}>
                <div style={{...styles.card, maxWidth: 440, textAlign: "center", padding: 32}}>
                    <div style={{fontSize: 48, marginBottom: 12}}>😕</div>
                    <h2 style={{margin: "0 0 8px", fontFamily: "'Fraunces', serif"}}>Invitation invalide</h2>
                    <p style={{color: C.textMuted, marginBottom: 20}}>{error}</p>
                    <button onClick={() => { window.history.pushState({}, "", "/"); window.location.reload(); }} style={styles.primaryButton}>
                        Retour à l'accueil
                    </button>
                </div>
            </div>
        );
    }

    if (joined) {
        return (
            <div style={{...styles.page, display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh"}}>
                <div style={{...styles.card, maxWidth: 440, textAlign: "center", padding: 32}}>
                    <div style={{fontSize: 48, marginBottom: 12}}>🎉</div>
                    <h2 style={{margin: "0 0 8px", fontFamily: "'Fraunces', serif"}}>{joinMessage}</h2>
                    <p style={{color: C.textMuted, marginBottom: 20}}>Vous pouvez maintenant participer aux discussions et sondages.</p>
                    <button onClick={onGoToApp} style={styles.primaryButton}>
                        Accéder au réseau
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div style={{...styles.page, display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh"}}>
            <div style={{...styles.card, maxWidth: 440, padding: 32}}>
                <div style={{textAlign: "center", marginBottom: 20}}>
                    <div style={{fontSize: 48, marginBottom: 8}}>✉️</div>
                    <h2 style={{margin: "0 0 4px", fontFamily: "'Fraunces', serif"}}>Vous êtes invité(e)</h2>
                    <p style={{color: C.textMuted, margin: 0}}>à rejoindre le collectif</p>
                </div>

                <div style={{
                    backgroundColor: C.primaryLight,
                    border: `1px solid ${C.primary}33`,
                    borderRadius: 12,
                    padding: 16,
                    marginBottom: 20,
                    textAlign: "center",
                }}>
                    <div style={{fontSize: 18, fontWeight: 700, color: C.primary, marginBottom: 4}}>
                        {invite.group_name}
                    </div>
                    {invite.group_description && (
                        <p style={{margin: "4px 0 0", fontSize: 13, color: C.textMuted}}>{invite.group_description}</p>
                    )}
                    <div style={{marginTop: 8, fontSize: 12, color: C.textMuted}}>
                        {invite.member_count} membre{invite.member_count > 1 ? "s" : ""} actif{invite.member_count > 1 ? "s" : ""}
                    </div>
                </div>

                {user ? (
                    <div style={{textAlign: "center"}}>
                        <p style={{fontSize: 14, marginBottom: 12}}>
                            Connecté en tant que <strong>{user.username}</strong>
                        </p>
                        <button onClick={handleJoin} disabled={joining} style={{...styles.primaryButton, width: "100%", padding: "12px 20px", fontSize: 16}}>
                            {joining ? "En cours..." : "Rejoindre ce collectif"}
                        </button>
                    </div>
                ) : (
                    <div>
                        {authMode === "login" && pendingVerification && (
                            <EmailVerificationNotice
                                email={pendingVerification.email}
                                pseudo={pendingVerification.pseudo}
                                mailFailed={pendingVerification.mailFailed}
                                onResend={async () => {
                                    const data = await apiResendVerification(pendingVerification.email);
                                    return data.message || "Si ce compte existe et n'est pas encore vérifié, un email a été envoyé.";
                                }}
                                nextStep="connectez-vous ici pour rejoindre le collectif"
                            />
                        )}
                        <div style={{display: "flex", gap: 0, marginBottom: 16}}>
                            <button
                                onClick={() => switchAuthMode("login")}
                                style={{
                                    flex: 1, padding: "8px 0", cursor: "pointer",
                                    fontWeight: 600, fontSize: 13,
                                    backgroundColor: authMode === "login" ? C.primary : "transparent",
                                    color: authMode === "login" ? "#fff" : C.textMuted,
                                    borderRadius: "8px 0 0 8px",
                                    border: `1px solid ${C.border}`,
                                }}
                            >Se connecter</button>
                            <button
                                onClick={() => switchAuthMode("register")}
                                style={{
                                    flex: 1, padding: "8px 0", cursor: "pointer",
                                    fontWeight: 600, fontSize: 13,
                                    backgroundColor: authMode === "register" ? C.primary : "transparent",
                                    color: authMode === "register" ? "#fff" : C.textMuted,
                                    borderRadius: "0 8px 8px 0",
                                    border: `1px solid ${C.border}`,
                                }}
                            >Créer un compte</button>
                        </div>

                        {authMode === "register" && (
                            <input
                                type="email"
                                placeholder="Email professionnel"
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                style={styles.input}
                            />
                        )}
                        {authMode === "login" ? (
                            <input
                                type="text"
                                placeholder="Pseudonyme"
                                value={username}
                                onChange={e => setUsername(e.target.value)}
                                style={styles.input}
                            />
                        ) : (
                            <>
                                <p style={{fontSize: 13, color: C.textMuted, margin: "0 0 8px"}}>
                                    Choisissez votre pseudo — il préserve votre anonymat
                                </p>
                                {loadingSuggestions ? (
                                    <p style={{fontSize: 13, color: C.textMuted, marginBottom: 12}}>Génération des suggestions…</p>
                                ) : suggestions.length === 0 ? (
                                    <PseudoWaitlistNotice email={email} onJoined={() => {}} />
                                ) : (
                                    <>
                                        <div style={{display: "flex", gap: 8, marginBottom: 8, flexWrap: "wrap"}}>
                                            {suggestions.map(s => (
                                                <button
                                                    key={s.pseudo}
                                                    type="button"
                                                    onClick={() => setSelectedPseudo(s)}
                                                    style={{
                                                        flex: "1 1 120px",
                                                        padding: "12px 8px",
                                                        borderRadius: 12,
                                                        border: selectedPseudo?.pseudo === s.pseudo
                                                            ? `2px solid ${C.primary}`
                                                            : `1px solid ${C.border}`,
                                                        background: `radial-gradient(circle at 35% 30%, ${s.couleur}CC, ${s.couleur}, ${s.couleur}88)`,
                                                        color: "white",
                                                        cursor: "pointer",
                                                        textAlign: "center",
                                                        transform: selectedPseudo?.pseudo === s.pseudo ? "scale(1.04)" : "scale(1)",
                                                        transition: "transform 0.15s, border-color 0.15s",
                                                        minHeight: "auto",
                                                        boxShadow: selectedPseudo?.pseudo === s.pseudo
                                                            ? `0 0 0 3px ${C.primaryLight}`
                                                            : "0 2px 6px rgba(0,0,0,0.12)",
                                                    }}
                                                >
                                                    <div style={{fontSize: 28, marginBottom: 4}}>{s.emoji}</div>
                                                    <div style={{fontSize: 13, fontWeight: 700, textShadow: "0 1px 2px rgba(0,0,0,0.3)"}}>{s.pseudo}</div>
                                                </button>
                                            ))}
                                        </div>
                                        <button type="button" onClick={refreshSuggestions}
                                                style={{...styles.button, fontSize: 12, marginBottom: 12}}>
                                            🔄 Autres suggestions
                                        </button>
                                    </>
                                )}
                            </>
                        )}
                        {showRegisterForm && (
                            <input
                                type="password"
                                placeholder="Mot de passe"
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                                style={styles.input}
                            />
                        )}
                        {showRegisterForm && authMode === "register" && password.length > 0 && (
                            <div style={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: "2px 12px", fontSize: 12, marginBottom: 10}}>
                                {[
                                    [pwdCheck.minLength, "8 caractères min."],
                                    [pwdCheck.hasUpper, "1 majuscule"],
                                    [pwdCheck.hasDigit, "1 chiffre"],
                                    [pwdCheck.hasSpecial, "1 caractère spécial"],
                                ].map(([ok, label]) => (
                                    <span key={label} style={{color: ok ? "#22C55E" : C.textMuted}}>
                                        {ok ? "✅" : "○"} {label}
                                    </span>
                                ))}
                            </div>
                        )}

                        {authMode === "register" && suggestions.length > 0 && (
                            <CguAcceptance accepted={cguAccepted} onChange={setCguAccepted}/>
                        )}

                        {authError && (
                            <p style={{color: C.danger, fontSize: 13, margin: "0 0 10px"}}>{authError}</p>
                        )}

                        <button
                            onClick={handleAuthThenJoin}
                            disabled={authMode === "register" && (!canRegister || suggestions.length === 0)}
                            style={{
                                ...styles.primaryButton, width: "100%", padding: "12px 20px", fontSize: 16,
                                opacity: authMode === "register" && (!canRegister || suggestions.length === 0) ? 0.5 : 1,
                                cursor: authMode === "register" && (!canRegister || suggestions.length === 0) ? "not-allowed" : "pointer",
                            }}
                        >
                            {authMode === "login" ? "Se connecter et rejoindre" : "Créer mon compte"}
                        </button>
                    </div>
                )}


                {joinMessage && !joined && (
                    <p style={{color: C.danger, fontSize: 13, marginTop: 10, textAlign: "center"}}>{joinMessage}</p>
                )}
            </div>
        </div>
    );
}
